import type { Router } from 'vue-router'

const RELOAD_GUARD_KEY = 'kargo:chunk-reload'

export type StaleClientRecoveryOptions = {
  onBeforeReload?: () => void
  reloadDelayMs?: number
}

export function isChunkLoadError(error: unknown): boolean {
  if (error == null) {
    return false
  }

  const message = (error instanceof Error ? error.message : String(error)).toLowerCase()
  const name = error instanceof Error ? error.name.toLowerCase() : ''

  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk') ||
    message.includes('chunkloaderror') ||
    name === 'chunkloaderror'
  )
}

export function clearStaleClientReloadGuard(): void {
  sessionStorage.removeItem(RELOAD_GUARD_KEY)
}

function reloadForStaleClient(targetUrl: string, options?: StaleClientRecoveryOptions): boolean {
  if (import.meta.env.DEV) {
    return false
  }

  if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
    return false
  }

  sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
  options?.onBeforeReload?.()

  const delay = options?.reloadDelayMs ?? 0
  const reload = (): void => {
    window.location.assign(targetUrl)
  }

  if (delay > 0) {
    window.setTimeout(reload, delay)
  } else {
    reload()
  }

  return true
}

export function registerStaleClientRecovery(
  router: Router,
  options?: StaleClientRecoveryOptions
): void {
  if (import.meta.env.DEV) {
    return
  }

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    reloadForStaleClient(window.location.href, options)
  })

  router.onError((error, to) => {
    if (!isChunkLoadError(error)) {
      return
    }
    reloadForStaleClient(to.fullPath, options)
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) {
      return
    }
    event.preventDefault()
    reloadForStaleClient(window.location.href, options)
  })
}
